// Renglón del «Libro de movimientos» de tarjetas PCF y su lectura multi-tienda.
import type { InventoryMovementType } from '@/api/services/retail-catalog/backoffice';
import { CardStockMovementTypeEnum } from '@fiado/type-kit/bin/retailCards/enums/CardStockMovementTypeEnum';
import type { CardStockMovementResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardStockMovementResponse';
import type { CardStockBalanceResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardStockBalanceResponse';
import { cardMovementReason } from '@/domains/retail/tarjetas/cardMovementReason';
import { CARD_MOVEMENT_TYPE_LABEL } from '@/domains/retail/tarjetas/constants';

export interface LedgerEntry {
    key: string;
    occurredAt: string;
    storeId: string;
    /** Tipo crudo del asiento (`ENTRY`, `IN_BATCH`, …) para filtrar sin depender de la etiqueta. */
    kind: InventoryMovementType | CardStockMovementTypeEnum;
    type: string;
    reason: string;
    /** Cantidad con signo (`+3`, `−1`). */
    qty: string;
    /** IMEI o serie; `null` si el movimiento no es por pieza. */
    identifier: string | null;
    author: string;
}

/** Asiento de tarjetas PCF de una tienda; el autor llega como email del operador. */
export function cardLedgerEntry(movement: CardStockMovementResponse, storeId: string): LedgerEntry {
    const magnitude = String(Math.abs(movement.quantity));
    return {
        key: `${storeId}#${movement.movementSk}`,
        occurredAt: movement.occurredAt,
        storeId,
        kind: movement.type,
        type: CARD_MOVEMENT_TYPE_LABEL[movement.type],
        reason: cardMovementReason(movement),
        qty: movement.quantity < 0 ? `−${magnitude}` : `+${magnitude}`,
        identifier: null,
        author: movement.actorUserId,
    };
}

/** Lectura de una tienda en una vuelta del libro; `balance` en `null` si nunca recibió un lote. */
export interface CardStoreRead {
    storeId: string;
    balance: CardStockBalanceResponse | null;
    movements: CardStockMovementResponse[];
    nextToken?: string;
}

/** Una vuelta: la página de cada tienda que todavía tenía movimientos. */
export interface CardLedgerRound {
    stores: CardStoreRead[];
}

/** Cursor de la siguiente vuelta: solo las tiendas con más páginas; `undefined` si ninguna. */
export function nextCardRound(round: CardLedgerRound): Record<string, string> | undefined {
    const next = round.stores.filter((s) => s.nextToken);
    if (next.length === 0) return undefined;
    return Object.fromEntries(next.map((s) => [s.storeId, s.nextToken as string]));
}

/**
 * Fecha más reciente entre los últimos asientos leídos de las tiendas que aún tienen páginas.
 * Lo anterior a esa marca puede tener huecos todavía; `null` si ninguna tienda tiene más.
 */
export function cardLedgerWatermark(rounds: readonly CardLedgerRound[]): string | null {
    const last = new Map<string, { more: boolean; oldest: string | undefined }>();
    for (const round of rounds) {
        for (const store of round.stores) {
            const oldest = store.movements.at(-1)?.occurredAt ?? last.get(store.storeId)?.oldest;
            last.set(store.storeId, { more: Boolean(store.nextToken), oldest });
        }
    }
    let mark: string | null = null;
    for (const { more, oldest } of last.values()) {
        if (more && oldest !== undefined && (mark === null || oldest > mark)) mark = oldest;
    }
    return mark;
}

/** Libro mezclado por fecha descendente, sin repetidos y cortado en la marca de agua. */
export function mergeCardLedger(rounds: readonly CardLedgerRound[]): LedgerEntry[] {
    const byKey = new Map<string, LedgerEntry>();
    for (const round of rounds) {
        for (const store of round.stores) {
            for (const movement of store.movements) {
                const entry = cardLedgerEntry(movement, store.storeId);
                byKey.set(entry.key, entry);
            }
        }
    }
    const mark = cardLedgerWatermark(rounds);
    return [...byKey.values()]
        .filter((e) => mark === null || e.occurredAt >= mark)
        .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

export interface CardStoreBalance {
    storeId: string;
    available: number;
    delivered: number;
    writtenOff: number;
}

/** Saldo por tienda de la primera vuelta; una tienda sin lotes cuenta en cero. */
export function cardStoreBalances(round: CardLedgerRound | undefined): CardStoreBalance[] {
    return (round?.stores ?? []).map((s) => ({
        storeId: s.storeId,
        available: s.balance?.available ?? 0,
        delivered: s.balance?.deliveredTotal ?? 0,
        writtenOff: s.balance?.writtenOffTotal ?? 0,
    }));
}

/** Totales de la cadena (o de la tienda filtrada). */
export function cardBalanceTotals(
    balances: readonly CardStoreBalance[],
): Omit<CardStoreBalance, 'storeId'> {
    return balances.reduce(
        (t, b) => ({
            available: t.available + b.available,
            delivered: t.delivered + b.delivered,
            writtenOff: t.writtenOff + b.writtenOff,
        }),
        { available: 0, delivered: 0, writtenOff: 0 },
    );
}

/** Últimas entradas de lote del libro ya leído. */
export function latestBatchEntries(entries: readonly LedgerEntry[], limit = 5): LedgerEntry[] {
    return entries.filter((e) => e.kind === CardStockMovementTypeEnum.IN_BATCH).slice(0, limit);
}
